import { useState, useEffect } from "react";
import { apiFetch } from "../../../context/AuthContext";

/**
 * useDashboardStats — one call to /employees/dashboard-stats.
 *
 * The endpoint computes every dashboard number from the SAME employee set
 * getEmployeesByVisibility returns for this caller, so a full admin sees
 * client-wide stats while a promoted user only sees their granted scope.
 * Consumers treat `stats` as an override: when null (endpoint failed),
 * the legacy per-endpoint values keep the dashboard working.
 */
// `viewType` ("admin" | "manager" | "user") is sent as the `x-view-type`
// header so the server scopes the aggregation to what the caller is
// currently looking at — the endpoint defaults to "admin" scope when the
// header is missing, which previously made this hook always return
// admin-wide numbers even while a promoted manager was on the "manager"
// view. Including viewType in the effect deps also makes the hook refetch
// the instant the parent shell's ViewTypeSwitcher changes, instead of
// requiring a page reload to pick up the new scope.
const useDashboardStats = (BASE_URL, viewType) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const clientName = (localStorage.getItem("clientName") || "").trim();
        const query = clientName ? `?clientName=${encodeURIComponent(clientName)}` : "";
        const resolvedViewType =
          viewType ||
          localStorage.getItem("managerViewType") ||
          localStorage.getItem("viewType") ||
          "admin";
        const res = await apiFetch(`${BASE_URL}/employees/dashboard-stats${query}`, {
          method: "GET",
          headers: { "x-view-type": resolvedViewType },
        });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && data?.success && data?.stats) {
          setStats(data.stats);
        } else if (!cancelled) {
          setStats(null);
        }
      } catch (err) {
        console.warn("dashboard-stats failed, falling back to legacy endpoints", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [BASE_URL, viewType]);

  return { stats, loading };
};

export default useDashboardStats;
